import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import React from 'react';
import { formatCurrencyInCents } from '../../helpers/formatters';
import { getPrinterBrandBadgeProps } from '../../helpers/printerBrand';
import {
    maintenanceCountLabel,
    replacementPartsCountLabel,
} from '../../helpers/printerMaintenances';
import { formatMaintenanceDurationLiteral } from '../../helpers/printersOverview';

function PrinterChip({ printer }) {
    if (!printer) {
        return (
            <Typography variant="body2" color="text.secondary">
                —
            </Typography>
        );
    }

    const brandBadge = getPrinterBrandBadgeProps(printer.brand);
    if (brandBadge) {
        return <Chip label={printer.name} size="small" {...brandBadge} />;
    }

    return (
        <Typography variant="subtitle2" fontWeight={700} noWrap>
            {printer.name}
        </Typography>
    );
}

function PrinterStatLine({ printer }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <PrinterChip printer={printer} />
            <Typography variant="body2" sx={{ mt: 0.5 }}>
                {maintenanceCountLabel(printer.maintenance_count)}
                {' · '}
                {formatMaintenanceDurationLiteral(printer.downtime_minutes)}
            </Typography>
        </Box>
    );
}

export default function MaintenanceOverviewCards({ stats }) {
    const {
        totalCount = 0,
        totalMinutes = 0,
        most = [],
        least = [],
        partsCount = 0,
        partsTotalCents = 0,
    } = stats || {};

    return (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <Card sx={{ flex: 1 }}>
                <CardContent>
                    <Typography color="text.secondary" variant="body2">
                        Total de manutenções
                    </Typography>
                    <Typography variant="h4" sx={{ my: 0.5 }}>
                        {maintenanceCountLabel(totalCount)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {totalMinutes > 0 ? formatMaintenanceDurationLiteral(totalMinutes) : '0min'}
                    </Typography>
                </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
                <CardContent>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                        Impressora com mais manutenção
                    </Typography>
                    {most.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                    ) : (
                        <Stack spacing={1.25}>
                            {most.map((printer) => (
                                <PrinterStatLine key={printer.printer_uuid} printer={printer} />
                            ))}
                        </Stack>
                    )}
                </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
                <CardContent>
                    <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                        Impressora com menos manutenção
                    </Typography>
                    {least.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                    ) : (
                        <Stack spacing={1.25}>
                            {least.map((printer) => (
                                <PrinterStatLine key={printer.printer_uuid} printer={printer} />
                            ))}
                        </Stack>
                    )}
                </CardContent>
            </Card>

            <Card sx={{ flex: 1 }}>
                <CardContent>
                    <Typography color="text.secondary" variant="body2">
                        Total de peças de reposição
                    </Typography>
                    <Typography variant="h4" sx={{ my: 0.5 }}>
                        {replacementPartsCountLabel(partsCount)}
                    </Typography>
                    {partsTotalCents > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {formatCurrencyInCents(partsTotalCents)}
                        </Typography>
                    )}
                </CardContent>
            </Card>
        </Stack>
    );
}
